/**
 * Blocked Users List Component
 * Displays and manages blocked users
 */

import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Avatar,
  Typography,
  Alert,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material'
import {
  LockOpen as UnblockIcon,
  Block as BlockIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { formatRelativeTime } from '@utils/dateUtils'

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #f093fb, #f5576c)',
  'linear-gradient(135deg, #4facfe, #00f2fe)',
  'linear-gradient(135deg, #43e97b, #38f9d7)',
  'linear-gradient(135deg, #fa709a, #fee140)',
  'linear-gradient(135deg, #a18cd1, #fbc2eb)',
  'linear-gradient(135deg, #fccb90, #d57eeb)',
]

function getAvatarGradient(name: string): string {
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]
}

interface BlockedUser {
  userId: string
  displayName: string
  avatar?: string
  homeTown?: string
  bio?: string
  isConnected: boolean
  connectionStatus: number
  blockedAt?: string
  rank?: {
    tier: number
    name: string
    pointsRequired: number
  }
}

interface BlockedUsersListProps {
  filterText?: string
}

export function BlockedUsersList({ filterText = '' }: BlockedUsersListProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: blockedUsersData, isLoading, error } = useQuery({
    queryKey: ['blockedUsers'],
    queryFn: socialApi.getBlockedUsers,
  })

  const blockedUsers = (blockedUsersData || []).filter((user: BlockedUser) => {
    if (!filterText) return true
    const query = filterText.toLowerCase()
    return (user.displayName || '').toLowerCase().includes(query) || (user.bio || '').toLowerCase().includes(query)
  })

  const unblockMutation = useMutation({
    mutationFn: (userId: string) => socialApi.unblockUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blockedUsers'] })
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    },
  })

  const handleUnblock = (e: React.MouseEvent, user: BlockedUser) => {
    e.stopPropagation()
    if (confirm(`Are you sure you want to unblock ${user.displayName}?`)) {
      unblockMutation.mutate(user.userId)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load blocked users'}
      </Alert>
    )
  }

  if (!blockedUsers || blockedUsers.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
        <BlockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No Blocked Users
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You haven't blocked anyone yet.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {blockedUsers.map((user: BlockedUser) => (
        <Box
          key={user.userId}
          onClick={() => navigate(`/profile/${user.userId}`)}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '14px',
            p: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            transition: 'background 0.15s',
            '&:hover': {
              filter: 'brightness(1.1)',
            },
          }}
        >
          <Avatar
            src={user.avatar}
            alt={user.displayName}
            sx={{
              width: 48,
              height: 48,
              background: user.avatar ? undefined : getAvatarGradient(user.displayName),
              fontSize: 18,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {user.displayName?.charAt(0).toUpperCase()}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user.displayName}
            </Typography>
            {user.blockedAt && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                Blocked {formatRelativeTime(user.blockedAt)}
              </Typography>
            )}
          </Box>

          <Box sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Unblock User">
              <Button
                size="small"
                onClick={(e) => handleUnblock(e, user)}
                disabled={unblockMutation.isPending}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '10px',
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: 'rgba(72,187,120,0.12)',
                  border: '1px solid rgba(72,187,120,0.25)',
                  color: 'success.main',
                  '&:hover': { bgcolor: 'rgba(72,187,120,0.2)' },
                }}
              >
                Unblock
              </Button>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </Box>
  )
}
